import { useRef, useEffect } from 'react'

interface CreateOption {
    icon: string
    label: string
    description: string
    value: string
}

interface CreateOptionsDropdownProps {
    isOpen: boolean
    onClose: () => void
    triggerRef: React.RefObject<HTMLButtonElement | null>
    options: CreateOption[]
    onOptionSelect: (value: string) => void
    title?: string
}

export function CreateOptionsDropdown({
    isOpen,
    onClose,
    triggerRef,
    options,
    onOptionSelect,
    title = 'Create',
}: CreateOptionsDropdownProps) {
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen, onClose, triggerRef])

    // Handle escape key to close dropdown
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const handleOptionClick = (value: string) => {
        onOptionSelect(value)
        onClose()
    }

    // Calculate position and clamp horizontally to avoid right overflow
    const triggerRect = triggerRef.current?.getBoundingClientRect()
    const top = triggerRect ? triggerRect.bottom + 16 : 0
    const estimatedDropdownWidth = dropdownRef.current?.offsetWidth ?? 320 // fallback to min-w-80 (20rem)
    const viewportWidth =
        typeof window !== 'undefined'
            ? window.innerWidth
            : estimatedDropdownWidth
    const padding = 32 // small margin from edges
    const maxLeft = Math.max(
        padding,
        viewportWidth - estimatedDropdownWidth - padding
    )
    const rawLeft = triggerRect ? triggerRect.left : 0
    const left = Math.min(Math.max(rawLeft, padding), maxLeft)

    return (
        <div
            ref={dropdownRef}
            className="fixed z-50 min-w-80 rounded-2xl border border-gray-200 bg-white p-4 shadow-lg"
            style={{
                top,
                left,
            }}
        >
            {/* Header */}
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-black">{title}</h3>
            </div>

            {/* Options */}
            <div className="space-y-3">
                {options.map((option) => (
                    <button
                        key={option.value}
                        onClick={() => handleOptionClick(option.value)}
                        className="flex w-full cursor-pointer items-start gap-3 rounded-xl p-3 text-left transition-colors hover:bg-gray-50"
                    >
                        <img
                            src={option.icon}
                            alt={option.label}
                            className="h-12 w-12"
                        />
                        <div className="flex-1">
                            <div className="text-base font-semibold text-black">
                                {option.label}
                            </div>
                            <div className="mt-1 text-sm text-gray-600">
                                {option.description}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    )
}
